import { useCallback, useContext, useMemo } from "react";
import { Route, useHistory, useLocation } from "react-router";

import { BTButton } from "@btnet/core-components/BTButton";

import { type BuilderInfo, type UserInfo } from "helpers/AppProvider.types";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { getBaseRoute } from "utilities/routes";
import { ASK_RECORD_PARAM, searchAskRoute } from "utilities/routes/pages/search.routes";

import { BTIconMagnifyingGlass } from "commonComponents/btWrappers/BTIcon";
import MainNavTooltip from "commonComponents/utilities/MainNavigation/common/MainNavTooltip";
import {
    formatOpenRecordParam,
    parseOpenRecord,
} from "commonComponents/utilities/MainNavigation/SearchDialog/entity/pageContext";
import { SearchBarRoutes } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBarRoutes";

import { type IAskHandoff, SearchDialog } from "./SearchDialog";
import { useSearchDialogOpen } from "./useSearchDialogOpen";

export interface ISearchDialogTriggerProps {
    builderInfo: BuilderInfo | undefined;
    userInfo: UserInfo;
}

/** The dialog's entry point in the main nav: a single search icon, no input. */
export function SearchDialogTrigger(props: ISearchDialogTriggerProps) {
    const { builderInfo, userInfo } = props;

    const { isOpen, open, close, trigger } = useSearchDialogOpen();
    const history = useHistory();
    const location = useLocation();
    const spaContext = useContext(SpaInfoContext);

    const openRecordEntity = useMemo(
        () => parseOpenRecord(location.pathname)?.entity ?? null,
        [location.pathname],
    );

    /**
     * Sends a question to the full-page Ask experience. Lives here because the
     * route is app knowledge and the dialog is a commonComponent.
     *
     * The open record is read now, while the location is still the page the
     * user asked from; the Ask page's own location says nothing about it.
     */
    const handleAskFullPage = useCallback(
        (question: string, handoff?: IAskHandoff) => {
            // No question opens Ask blank, rather than on an empty `?q=`.
            const params = new URLSearchParams();
            if (question.length > 0) {
                params.set("q", question);
            }
            const record = handoff?.aboutOpenRecord ? parseOpenRecord(location.pathname) : null;
            if (record !== null) {
                params.set(ASK_RECORD_PARAM, formatOpenRecordParam(record));
            }
            const search = params.toString();
            let path = searchAskRoute.path;
            if (search.length > 0) {
                path += `?${search}`;
            }
            if (spaContext.isSpa) {
                history.push(path);
            } else {
                window.location.assign(getBaseRoute() + path);
            }
        },
        [history, location.pathname, spaContext.isSpa],
    );

    return (
        <>
            {/* The hotkey is the faster way in, and an icon cannot say so on
                its own. Off while open, where the scrim covers the icon. `flex`
                keeps the span it wraps the button in from adding a line box. */}
            <MainNavTooltip title="Search (Ctrl+K)" className="flex" enabled={!isOpen}>
                <BTButton
                    className="main-navigation-button"
                    data-testid="search-main-nav-button"
                    aria-label="Search"
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-keyshortcuts="Control+K Meta+K"
                    onClick={open}
                    icon={<BTIconMagnifyingGlass />}
                />
            </MainNavTooltip>
            {isOpen && (
                <SearchDialog
                    onClose={close}
                    userId={userInfo.userId ?? 0}
                    builderInfo={builderInfo}
                    openRecordEntity={openRecordEntity}
                    trigger={trigger}
                    onAskFullPage={handleAskFullPage}
                />
            )}
            {/* Mounted outside the dialog, and unconditionally, so a pushed
                /SearchResult/* route still renders its entity modal after the
                dialog closes — closing the dialog is how you get TO that
                modal, so it cannot own the routes. */}
            <Route render={(routeProps) => <SearchBarRoutes {...routeProps} />} />
        </>
    );
}
